/**
 * Rendert ein geparstes Lead Sheet als DOM.
 *
 * Aufbau pro Textzeile:
 *   <div class="sheet-line">
 *     <span class="pair"><span class="pair__chord">Dm7</span><span class="pair__text">Wort </span></span>
 *     ...
 *   </div>
 *
 * Jedes Wort wird zu einem eigenen inline-block. Dadurch bricht eine lange
 * Zeile sauber um, ohne dass ein Akkord von seinem Wort getrennt wird.
 */
import type { GridLine, ParsedSong, SheetLine } from './parse.ts';

export interface RenderResult {
  /** Alle Zeilen-Elemente in Reihenfolge – Ankerpunkte fuer Anmerkungen. */
  lineEls: HTMLElement[];
}

/* ------------------------------------------------ Akkord-Satz
 *
 * Wie in gedruckten Real Books: der Grundton gross, alles Weitere kleiner
 * und leicht hochgestellt – "C" gross, "maj7" klein daneben. Das Moll-"m"
 * bleibt auf der Grundlinie, weil es zum Namen gehoert, nicht zur Farbe.
 * b und # werden zu echten Vorzeichen (♭ ♯).
 */

const CHORD_PARTS = /^([A-Ga-g])([#b♯♭]?)(m(?!aj)|min|-)?(.*?)(?:\/([A-Ga-g][#b♯♭]?))?$/;

function accidentals(text: string): string {
  return text.replace(/#/g, '♯').replace(/b(?=\d|$|\))/g, '♭');
}

/** Zerlegt einen Akkord fuer den Satz; null bei Haltezeichen o. Ae. */
export function chordParts(chord: string): { root: string; minor: string; ext: string; bass: string } | null {
  const m = CHORD_PARTS.exec(chord.trim());
  if (!m) return null;
  const [, letter = '', acc = '', minor = '', ext = '', bass = ''] = m;
  return {
    root: letter.toUpperCase() + acc.replace('#', '♯').replace('b', '♭'),
    minor: minor === '-' ? '–' : minor,
    ext: accidentals(ext),
    bass: bass ? bass.charAt(0).toUpperCase() + bass.slice(1).replace('#', '♯').replace('b', '♭') : '',
  };
}

/** Schreibt einen Akkord gesetzt in ein Element (oder als Text, falls kein Akkord). */
export function setChord(el: HTMLElement, chord: string): void {
  const parts = chordParts(chord);
  if (!parts) {
    el.textContent = chord;
    return;
  }
  el.textContent = '';
  el.append(document.createTextNode(parts.root + parts.minor));
  if (parts.ext) {
    const ext = document.createElement('span');
    ext.className = 'chord-ext';
    ext.textContent = parts.ext;
    el.append(ext);
  }
  if (parts.bass) {
    const bass = document.createElement('span');
    bass.className = 'chord-bass';
    bass.textContent = '/' + parts.bass;
    el.append(bass);
  }
}

/**
 * Passt die Schrift in jedem Takt an, der seine Akkorde nicht fasst:
 * lieber kleiner als abgeschnitten oder ueber den Taktstrich hinaus.
 */
export function fitGridBars(root: HTMLElement): void {
  for (const bar of root.querySelectorAll<HTMLElement>('.grid-bar')) {
    bar.style.fontSize = '';
    // Zweimal messen: nach dem Verkleinern schrumpfen auch Abstaende, die
    // am Schriftgrad haengen – der zweite Schritt trifft dann genau.
    for (let pass = 0; pass < 2; pass++) {
      const inner = bar.scrollWidth;
      const room = bar.clientWidth;
      if (!(inner > room + 1 && room > 0)) break;
      const current = parseFloat(bar.style.fontSize) || 1;
      bar.style.fontSize = `${Math.max(0.5, current * (room / inner) * 0.97).toFixed(3)}em`;
    }
  }
}

/** Neu einpassen, sobald sich die Breite aendert (Drehen, Seitenleiste, A−/A+). */
const watched = new WeakSet<HTMLElement>();
function watchFit(target: HTMLElement): void {
  if (watched.has(target) || typeof ResizeObserver === 'undefined') return;
  watched.add(target);
  let frame = 0;
  new ResizeObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => fitGridBars(target));
  }).observe(target);
}

/** Zerlegt Text in Woerter und behaelt die Leerzeichen am Wortende. */
function splitWords(text: string): string[] {
  if (text === '') return [''];
  const parts = text.match(/\S+\s*|\s+/g);
  return parts ?? [text];
}

function renderRow(line: Extract<SheetLine, { kind: 'row' }>): HTMLElement {
  const el = document.createElement('div');
  el.className = 'sheet-line' + (line.chordsOnly ? ' sheet-line--chords' : '');

  for (const pair of line.pairs) {
    const words = line.chordsOnly ? [''] : splitWords(pair.text);
    words.forEach((word, index) => {
      const span = document.createElement('span');
      span.className = 'pair';
      const chord = document.createElement('span');
      chord.className = 'pair__chord';
      // Der Akkord haengt am ersten Wort des Abschnitts.
      if (index === 0 && pair.chord) setChord(chord, pair.chord);
      const text = document.createElement('span');
      text.className = 'pair__text';
      // Geschuetztes Leerzeichen haelt die Zeilenhoehe, wenn kein Text da ist.
      text.textContent = word === '' ? ' ' : word;
      span.append(chord, text);
      el.append(span);
    });
  }
  if (line.pairs.length === 0) el.innerHTML = '&nbsp;';
  return el;
}

/**
 * Rendert eine Taktrasterzeile: Beschriftung links, Takte in Kästchen
 * daneben, Wiederholung rechts. Entspricht dem, was man von Hand aufs
 * Blatt schreibt.
 */
function renderGrid(line: GridLine): HTMLElement {
  const el = document.createElement('div');
  el.className = 'grid-row';

  const label = document.createElement('div');
  label.className = 'grid-label';
  label.textContent = line.label ?? '';

  const bars = document.createElement('div');
  bars.className = 'grid-bars';
  for (const cell of line.bars) {
    const bar = document.createElement('div');
    bar.className = 'grid-bar';
    if (cell.length === 0) {
      // Leerer Takt: geschütztes Leerzeichen hält die Höhe.
      bar.textContent = '\u00a0';
    } else {
      for (const chord of cell) {
        const span = document.createElement('span');
        span.className = 'grid-chord';
        setChord(span, chord);
        bar.append(span);
      }
    }
    bars.append(bar);
  }

  const repeat = document.createElement('div');
  repeat.className = 'grid-repeat';
  repeat.textContent = line.repeat ?? '';

  el.append(label, bars, repeat);
  return el;
}

export function renderSheet(parsed: ParsedSong, target: HTMLElement): RenderResult {
  target.textContent = '';
  const lineEls: HTMLElement[] = [];

  parsed.lines.forEach((line, index) => {
    let el: HTMLElement;
    switch (line.kind) {
      case 'row':
        el = renderRow(line);
        break;
      case 'grid':
        el = renderGrid(line);
        break;
      case 'label':
        el = document.createElement('div');
        el.className = 'sheet-label';
        el.textContent = line.text;
        break;
      case 'comment':
        el = document.createElement('div');
        el.className = 'sheet-comment';
        el.textContent = line.text;
        break;
      case 'spacer':
      default:
        el = document.createElement('div');
        el.className = 'sheet-spacer';
        break;
    }
    if (line.section !== 'none') el.dataset.section = line.section;
    el.dataset.line = String(index);
    target.append(el);
    lineEls.push(el);
  });

  // Erst nach dem Layout messbar: dann enge Takte einpassen – und noch
  // einmal, sobald die Schriften geladen sind (sie aendern die Breiten).
  requestAnimationFrame(() => fitGridBars(target));
  void document.fonts?.ready.then(() => requestAnimationFrame(() => fitGridBars(target)));
  watchFit(target);

  return { lineEls };
}
